import { useRef, useEffect, useMemo, useCallback } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import { PivotControls } from "@react-three/drei";
import * as THREE from "three";

const PLANE_SIZE = 30;
const PLANE_COLOR = "#44aaff";

// Reusable temporaries (avoid per-frame allocations)
const _normal = new THREE.Vector3();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();

/**
 * A free-form 3D clipping plane with a PivotControls gizmo.
 *
 * Uses localClippingEnabled + per-material clippingPlanes so the gizmo
 * and plane visual are never clipped by their own plane.
 */
export function ClipPlane3D({ active, flipped, onDragStart, onDragEnd, initialMatrix, onMatrixChange }: {
  active: boolean;
  flipped: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  initialMatrix?: number[];
  onMatrixChange?: (elements: number[]) => void;
}) {
  const { gl, scene } = useThree();
  const pivotRef = useRef<THREE.Group>(null);
  const groupRef = useRef<THREE.Group>(null);
  const clipPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 0, 1), 0));
  const clipArray = useMemo(() => [clipPlane.current], []);

  // Ring geometry (computed once)
  const ringPositions = useMemo(() => {
    const arr = new Float32Array(65 * 3);
    for (let i = 0; i < 65; i++) {
      const a = (i / 64) * Math.PI * 2;
      arr[i * 3] = Math.cos(a) * PLANE_SIZE;
      arr[i * 3 + 1] = Math.sin(a) * PLANE_SIZE;
      arr[i * 3 + 2] = 0;
    }
    return arr;
  }, []);

  // Enable localClippingEnabled on the renderer
  useEffect(() => {
    gl.localClippingEnabled = active;
    // Always clear global clipping planes — we use per-material only
    gl.clippingPlanes = [];
    return () => {
      gl.localClippingEnabled = false;
      gl.clippingPlanes = [];
    };
  }, [active, gl]);

  // Check if a node is a descendant of the pivot controls group
  const isGizmoChild = useCallback((obj: THREE.Object3D): boolean => {
    if (!pivotRef.current) return false;
    let cur: THREE.Object3D | null = obj;
    while (cur) {
      if (cur === pivotRef.current) return true;
      cur = cur.parent;
    }
    return false;
  }, []);

  // Each frame: update the clip plane, then apply/remove clippingPlanes on scene materials
  useFrame(() => {
    if (!active) return;

    // Update clip plane from gizmo transform
    if (groupRef.current) {
      groupRef.current.updateWorldMatrix(true, false);
      groupRef.current.getWorldQuaternion(_quat);
      groupRef.current.getWorldPosition(_pos);
      _normal.set(0, 0, flipped ? -1 : 1).applyQuaternion(_quat);
      clipPlane.current.setFromNormalAndCoplanarPoint(_normal, _pos);
    }

    // Traverse scene and set clippingPlanes on all materials except gizmo children
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh | THREE.Points | THREE.Line;
      if (!mesh.material) return;

      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const isGizmo = isGizmoChild(obj);

      for (const mat of materials) {
        if (isGizmo) {
          // Gizmo materials: no clipping
          if (mat.clippingPlanes !== null && mat.clippingPlanes?.length !== 0) {
            mat.clippingPlanes = [];
            mat.needsUpdate = true;
          }
        } else {
          // Scene materials: apply clip plane
          if (mat.clippingPlanes !== clipArray) {
            mat.clippingPlanes = clipArray;
            mat.needsUpdate = true;
          }
        }
      }
    });
  });

  // Cleanup: remove clippingPlanes from all materials when deactivated
  useEffect(() => {
    if (!active) {
      scene.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (!mesh.material) return;
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const mat of materials) {
          if (mat.clippingPlanes && mat.clippingPlanes.length > 0) {
            mat.clippingPlanes = [];
            mat.needsUpdate = true;
          }
        }
      });
    }
  }, [active, scene]);

  // Restore initial matrix if provided
  const matrixProp = useMemo(() => {
    if (!initialMatrix || initialMatrix.length !== 16) return undefined;
    const m = new THREE.Matrix4();
    m.fromArray(initialMatrix);
    return m;
  }, []); // only on mount

  if (!active) return null;

  return (
    <group ref={pivotRef}>
      <PivotControls
        scale={4}
        lineWidth={2}
        depthTest={false}
        disableScaling
        renderOrder={999}
        axisColors={["#ff4060", "#40df80", "#4080ff"]}
        hoveredColor="#ffff40"
        onDragStart={onDragStart}
        onDragEnd={() => {
          onDragEnd?.();
          if (onMatrixChange && groupRef.current) {
            groupRef.current.updateWorldMatrix(true, false);
            onMatrixChange(Array.from(groupRef.current.matrixWorld.elements));
          }
        }}
        {...(matrixProp ? { matrix: matrixProp } : {})}
      >
        <group ref={groupRef}>
          {/* Semi-transparent disc showing the clip plane */}
          <mesh renderOrder={998}>
            <circleGeometry args={[PLANE_SIZE, 64]} />
            <meshBasicMaterial
              color={PLANE_COLOR}
              transparent
              opacity={0.12}
              side={THREE.DoubleSide}
              depthWrite={false}
            />
          </mesh>
          {/* Edge ring for visibility */}
          <lineLoop renderOrder={998}>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                args={[ringPositions, 3]}
              />
            </bufferGeometry>
            <lineBasicMaterial
              color={PLANE_COLOR}
              transparent
              opacity={0.5}
              depthWrite={false}
            />
          </lineLoop>
        </group>
      </PivotControls>
    </group>
  );
}
